/**
 * +quests: Mossvale's side-quest NPCs (docs/design/zones.md "Side quests"): eighteen locals with a
 * running gag each, two or three per area, standing at the edge of their area by the road, out of
 * every mob's aggro reach (the content test checks it against the spawns and their patrols).
 *
 * This module attaches them to `mossvale.field.npcs` when it is imported (the zone index imports it
 * after the gathering nodes, so node placement is untouched) and keeps mossvaleZone.ts untouched.
 * Their looks (model kit, tint, prop) are the renderer's (apps/web render/toon/outdoor/questNpcLooks.ts).
 */
import type { FieldNpcDef, Vec2 } from '@keystone/sim';
import { angleTo } from '../dungeons/geometry';
import { mossvale } from './fields';
import { ZONE_SPOTS } from './mossvaleZone';
import type { QuestNpcVoice } from './questTypes';

/** Side-quest NPC ids (FieldNpcDef ids on the Mossvale map). A shared contract: add freely, never rename. */
export const QUEST_NPC = {
  // The Slime Hollows
  hobb: 'qn_hobb',
  pell: 'qn_pell',
  pip: 'qn_pip',
  // Brookside Farm and the Boar Run
  stuffins: 'qn_stuffins',
  bessa: 'qn_bessa',
  // Barrowfield
  moll: 'qn_moll',
  fennick: 'qn_fennick',
  // Glimmermere and the Lakeside Outpost
  wendel: 'qn_wendel',
  tilly: 'qn_tilly',
  percy: 'qn_percy',
  // Greyhorn Heights and Tallowdeep
  hesk: 'qn_hesk',
  ottilie: 'qn_ottilie',
  wick: 'qn_wick',
  // Bramblewood
  kip: 'qn_kip',
  barnaby: 'qn_barnaby',
  // The Pale Rise and the Tower Road
  waverly: 'qn_waverly',
  agatha: 'qn_agatha',
  bartram: 'qn_bartram',
} as const;

const r3 = (v: number): number => Math.round(v * 1000) / 1000;
const npc = (id: string, name: string, title: string, pos: Vec2, faceTo: Vec2): FieldNpcDef => ({ id, name, title, role: 'questgiver', pos, facing: r3(angleTo(pos, faceTo)), model: 'quest' });

/** Where each one stands (picked clear of spawns, patrols, nodes, roads and other NPCs; see mossvaleQuests.test.ts). */
export const QUEST_NPCS: readonly FieldNpcDef[] = [
  npc(QUEST_NPC.hobb, 'Farmer Hobb Turvey', 'Missing Socks', { x: 300, y: 128.5 }, { x: 330, y: 135 }),
  npc(QUEST_NPC.pell, 'Granny Pell', 'Knits for Slimes', ZONE_SPOTS.pell, ZONE_SPOTS.start), // +spawn: at her cottage by the new heroes' start
  npc(QUEST_NPC.pip, 'Pip Brightwhistle', 'Slime Scholar', { x: 260.5, y: 145.5 }, { x: 275, y: 135 }),
  npc(QUEST_NPC.stuffins, 'Mister Stuffins', 'Retired Scarecrow', { x: 192.5, y: 295.5 }, { x: 200, y: 285 }),
  npc(QUEST_NPC.bessa, 'Bessa Plum', 'Turnip Enthusiast', { x: 139, y: 256.5 }, { x: 150, y: 262 }),
  npc(QUEST_NPC.moll, 'Moll Spadework', 'Gravedigger', { x: 415, y: 369 }, { x: 422, y: 360 }),
  npc(QUEST_NPC.fennick, 'Brother Fennick', 'Ghost Enthusiast', { x: 429.5, y: 392.5 }, { x: 421, y: 400 }),
  npc(QUEST_NPC.wendel, 'Wendel Sprat', 'Feuding Fisherman', { x: 626.5, y: 307 }, { x: 700, y: 262 }),
  npc(QUEST_NPC.tilly, 'Cook Tilly Marrow', 'Squeamish Cook', { x: 609, y: 341.5 }, { x: 612, y: 330 }),
  npc(QUEST_NPC.percy, 'Percival Pennyfeather', 'Tourist', { x: 626.5, y: 342 }, { x: 612, y: 330 }),
  npc(QUEST_NPC.hesk, 'Miner Hesk Dunlow', 'Music Critic', { x: 734, y: 664.5 }, { x: 742, y: 660 }),
  npc(QUEST_NPC.ottilie, 'Shepherd Ottilie', 'Goat Wrangler', { x: 653.5, y: 560 }, { x: 662, y: 566 }),
  npc(QUEST_NPC.wick, 'Wicksworth', 'Candle Connoisseur', { x: 697, y: 601 }, { x: 701, y: 611 }),
  npc(QUEST_NPC.kip, 'Kip Sallow', 'Aspiring Main Bandit', { x: 202.5, y: 588 }, { x: 205, y: 570 }),
  npc(QUEST_NPC.barnaby, 'Barnaby Ashgrove', 'Woodcutter (No Axe)', { x: 312.5, y: 458.5 }, { x: 322, y: 462 }),
  npc(QUEST_NPC.waverly, 'Sir Waverly', 'Somewhat Dead', { x: 430, y: 537.5 }, { x: 433, y: 545 }),
  npc(QUEST_NPC.agatha, 'Professor Agatha Dustwhistle', 'Rock Expert', { x: 419, y: 526 }, { x: 430, y: 522 }),
  npc(QUEST_NPC.bartram, 'Clerk Bartram Quill', 'Tower Paperwork', { x: 451, y: 770 }, { x: 462, y: 786 }),
];

/** Greetings and barks (short, silly, never mean). */
export const QUEST_NPC_VOICES: readonly QuestNpcVoice[] = [
  {
    npcId: QUEST_NPC.hobb,
    greeting: 'Psst. You. Have you seen a sock? Wool, left foot, slightly damp?',
    barks: ['Eleven socks. ELEVEN.', 'They wobble so innocently. Too innocently.', 'My feet are cold and my heart is colder.', 'Check the slimes. Always check the slimes.'],
    done: 'The dog. It was the dog all along. I owe those slimes an apology.',
  },
  {
    npcId: QUEST_NPC.pell,
    greeting: 'Come in, come in. Mind the yarn. And the slimes. And the yarn on the slimes.',
    barks: ['Knit one, purl one, wobble one.', 'Slimes have no feet, you say? They have potential, dearie.', 'Wool keeps everything warm. Even regret.'],
    done: 'I am knitting the slimes little hats next. Don’t tell Hobb.',
  },
  {
    npcId: QUEST_NPC.pip,
    greeting: 'I’m writing a book about slimes. It’s called "Slimes". Chapter one is also called "Slimes".',
    barks: ['Hypothesis: slimes are just jelly that got ambitious.', 'I licked one. For science. Do not lick one.', 'Page forty-two: they are VERY sticky.'],
    done: 'Chapter two is called "More Slimes". It’s going to be a hit.',
  },
  {
    npcId: QUEST_NPC.stuffins,
    greeting: 'Don’t be alarmed. I’m a scarecrow. Well, I was. I’ve moved into consulting.',
    barks: ['I quit. The crows were too clever. They had a system.', 'Straw is not a career. Straw is a lifestyle.', 'Those crows down there are big, round and grunty. My kind of crow.'],
    done: 'Business is booming. The crows fear me now. The round ones, anyway.',
  },
  {
    npcId: QUEST_NPC.bessa,
    greeting: 'Shh. The turnips are sleeping. That one’s Doris. That one’s also Doris.',
    barks: ['Every turnip has a name. And a dream.', 'Gerald would have been a beautiful soup.', 'Never trust anything pink that sneezes.'],
    done: 'Gerald Two is coming along nicely. He has his father’s leaves.',
  },
  {
    npcId: QUEST_NPC.moll,
    greeting: 'I dig holes. Then I fill them in. Lately something keeps digging them OUT.',
    barks: ['Dig, fill, dig, fill. Fill, fill, fill.', 'A boar dug up old Uncle Wendham. He looked surprised.', 'Truffles. It’s always truffles.'],
    done: 'Quiet night tonight. I might dig a hole just for fun.',
  },
  {
    npcId: QUEST_NPC.fennick,
    greeting: 'Hello! Are you a ghost? You can tell me. I won’t be weird about it.',
    barks: ['Oooooo. That’s how ghosts talk. I’ve been practising.', 'Something pale walks here at midnight. I KNOW it.', 'If you see a ghost, tell it I said hi.'],
    done: 'I slept so well last night. Woke up in the graveyard, oddly refreshed.',
  },
  {
    npcId: QUEST_NPC.wendel,
    greeting: 'You look like someone who’s never been personally insulted by a fish.',
    barks: ['Clive. CLIVE.', 'He took my bait, my hook and my dignity. In that order.', 'Snappers don’t smirk. Clive smirks.'],
    done: 'Clive sent me a postcard. It just says "nice try". I’m having it framed.',
  },
  {
    npcId: QUEST_NPC.tilly,
    greeting: 'Welcome to the outpost kitchen! Today’s special is soup. Every day’s special is soup.',
    barks: ['Don’t look in the pot. I never look in the pot.', 'A good soup needs love. And legs, apparently.', 'I fainted peeling a potato once. It had eyes.'],
    done: 'Soup’s on! It’s leg-free. Mostly. Don’t ask.',
  },
  {
    npcId: QUEST_NPC.percy,
    greeting: 'My guidebook calls Glimmermere "a gentle lake of huggable wildlife". I’ve been bitten four times.',
    barks: ['Page twelve says the snappers enjoy cuddles. Page twelve is wrong.', 'I’m writing a very strongly worded postcard.', 'Is this the scenic route? It feels bitey.'],
    done: 'Next year I’m going somewhere with fewer teeth. Possibly a library.',
  },
  {
    npcId: QUEST_NPC.hesk,
    greeting: 'Hear that? That’s the Tallow Chanters. Singing. Badly. On PURPOSE.',
    barks: ['That’s not a note. That’s a cry for help.', 'Nobody is that flat by accident.', 'I used to love music. Then I moved next to a mine.'],
    done: 'I’ve started a choir. We’re terrible, but at least it’s an accident.',
  },
  {
    npcId: QUEST_NPC.ottilie,
    greeting: 'Oh, hello! Have you met Margaret? She’s the goat on that rock. And that rock. She gets around.',
    barks: ['Margaret! Off the cliff, please! Thank you, Margaret.', 'The goats climb, the hounds chase, and I worry.', 'Every goat needs a bell. And a hobby.'],
    done: '*ding* That’s Margaret saying hello. She’s on the roof again.',
  },
  {
    npcId: QUEST_NPC.wick,
    greeting: 'A candle is a little sun you can hold. Unless the Tallow folk made it. Then it’s a little disappointment.',
    barks: ['Lumpy. Smoky. Smells of feet. Tallow candles, everybody.', 'I sniff candles professionally.', 'A wick should stand proud, not slouch.'],
    done: 'I’ve started a new line: "Rustic Lump". It’s selling very well.',
  },
  {
    npcId: QUEST_NPC.kip,
    greeting: 'Oi. You. Do I look like the main bandit? Be honest. Be honest and say yes.',
    barks: ['Main bandit. That’s me. Nearly.', 'I’ve got a bandana and everything. It’s a bit small.', 'Step one: be a bandit. Step two: be the MAIN bandit. Step three: unclear.'],
    done: 'Main bandit Kip, at your service! Sorry. I mean: stand and deliver! Please.',
  },
  {
    npcId: QUEST_NPC.barnaby,
    greeting: 'Morning! Don’t suppose you’ve seen an axe? Or seven? Big wolves carrying them?',
    barks: ['The wolves stole my axes. They think it’s fetch.', 'I can’t cut wood with a stern look. I’ve tried.', 'Good doggies. BAD doggies. Good doggies?'],
    done: 'Forty-one handles and counting. I might open a museum.',
  },
  {
    npcId: QUEST_NPC.waverly,
    greeting: 'Oh! Hello! Terribly sorry, I’m a ghost. Don’t mind me. Lovely weather for being alive, isn’t it?',
    barks: ['Cheer up, chaps! Oh, they never do.', 'Being dead is fine. It’s the moping I can’t stand.', 'Three hundred years on this hill. Smashing view.'],
    done: 'My nightlight is wonderful. I haven’t been scared of the dark for a whole week.',
  },
  {
    npcId: QUEST_NPC.agatha,
    greeting: 'Careful! You’re standing on an artifact. And that’s an artifact. Everything here is an artifact.',
    barks: ['This rock is clearly ancient. Look how rocky it is.', 'Do not touch the artifacts. That includes the ground.', 'I’ve catalogued four thousand stones. Three are interesting.'],
    done: 'I’ve named a pebble after you. It’s my second-favourite pebble.',
  },
  {
    npcId: QUEST_NPC.bartram,
    greeting: 'Welcome to the Tower Road. Have you filled in form 7B? Nobody has filled in form 7B.',
    barks: ['Please take a number. The number is one. It’s always one.', 'Stamp, stamp. That’s the sound of progress.', 'The Tower is open to anyone. With the correct forms.'],
    done: 'Your file is in order. Your file is the only thing in order.',
  },
];

const VOICES: ReadonlyMap<string, QuestNpcVoice> = new Map(QUEST_NPC_VOICES.map((v) => [v.npcId, v]));

/** A side-quest NPC's voice (greeting, barks), or undefined for other NPCs. */
export function questNpcVoice(npcId: string): QuestNpcVoice | undefined {
  return VOICES.get(npcId);
}

/** Is this FieldNpcDef id one of the side-quest NPCs? */
export function isQuestNpc(npcId: string): boolean {
  return VOICES.has(npcId);
}

// Attach to the zone map once (the module is evaluated once; the guard keeps hot reloads idempotent).
const field = mossvale.field!;
const npcs = (field.npcs ??= []);
for (const n of QUEST_NPCS) if (!npcs.some((x) => x.id === n.id)) npcs.push(n);
